import { Component } from 'react';
import styled from 'styled-components';
import {
  Contribution,
  ExistingMetadata,
  MetadataFeedbackStatus,
  NewMetadata,
} from '../../../../../../../common/model/input/contributions/types';
import { ModalCompanyMetadatum, ModalOptions } from '../../../../../../untidy/redux/modules/modal';
import { aiName } from '../../../../../../untidy/texts';
import { TextField } from '../../../../../generic/components/inputs/text-field';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { themeDEPRECATED } from '../../../../../generic/global/styles';
import { replaceOrAddToArray } from '../../../../../generic/utilities/utils';
import { ContributionModalParagraph, ContributionModalTitle, FormRow, FullRow, RemoveLink } from './common';
import { FormButton } from './form-button';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
import * as reactRadioButtons from 'react-radio-buttons';
const { RadioButton, RadioGroup } = reactRadioButtons;

const Section = styled.div`
  margin-bottom: ${themeDEPRECATED.rhythm(5.5)};
`;

const SectionTitle = styled.h3`
  margin-bottom: ${themeDEPRECATED.rhythm(3)};
  margin-top: ${themeDEPRECATED.rhythm(5)};
  ${themeDEPRECATED.fontSize(3)};
`;

const Instructions = styled.h3`
  margin-bottom: ${themeDEPRECATED.rhythm(4)};
`;

const DataPointRow = styled(FormRow)`
  margin-top: 0;
  margin-bottom: ${themeDEPRECATED.rhythm(4.5)};
  /*padding-left: ${themeDEPRECATED.rhythm(4)};*/
  padding-left: 0;
`;

const Source = styled.span`
  color: ${themeDEPRECATED.colors.grayWarm()};
  position: relative;
  left: 6px;
`;

const DataPointTitle = styled.div`
  ${themeDEPRECATED.fontSemiBold};
  margin-bottom: ${themeDEPRECATED.rhythm(3.5)};
`;

const NewDataPointRow = styled.div`
  padding-left: ${themeDEPRECATED.rhythm(4)};
  margin-bottom: ${themeDEPRECATED.rhythm(5)};
`;

const NewDataPointTitle = styled.h3`
  margin-top: ${themeDEPRECATED.rhythm(4.5)};
  margin-bottom: ${themeDEPRECATED.rhythm(2)};
`;

const NewDataPointLink = styled.span`
  cursor: pointer;
`;

interface State {
  comment?: string;
  existingMetadata?: ExistingMetadata[];
  newMetadata?: NewMetadata[];
}

interface Props {
  isSubmitting: boolean;
  options: ModalOptions;
  submitContribution: (options: { target?: string; data: Contribution }) => void;
}

export class CompanyMetadataForm extends Component<Props, State> {
  public state: State = {};

  public render() {
    const { isSubmitting, options } = this.props;
    const { comment, existingMetadata, newMetadata } = this.state;

    if (!options.existingData) {
      console.error('Missing required metadata for company');
      return null;
    }
    const {
      target,
      existingData: { revenue, employees },
    } = options;

    if (!revenue || !employees) {
      console.error('Missing required metadata for company');
      return null;
    }

    const canSubmit =
      !newMetadata || newMetadata.every(d => !!d.source && !!d.type && (d.value as string | null) != null && !!d.year);

    const sections: Array<{
      title: string;
      type: 'revenue' | 'employees';
      data: ModalCompanyMetadatum[];
      unit?: string;
      formatNumber: (num: number) => string;
    }> = [
      {
        title: 'Revenue',
        data: revenue,
        type: 'revenue',
        unit: 'USD',
        formatNumber: num => `$${num.toLocaleString()}`,
      },
      {
        title: 'Employees',
        data: employees,
        type: 'employees',
        formatNumber: num => `${num.toLocaleString()}`,
      },
    ];

    return (
      <>
        <ContributionModalTitle>
          Does something look weird in these numbers? Or do you have better insight?
        </ContributionModalTitle>
        <form
          onSubmit={e => {
            e.preventDefault();
          }}
        >
          {sections.map(section => {
            const sectionNewData = newMetadata && newMetadata.filter(d => d.type === section.type);
            return (
              <Section key={section.type}>
                <SectionTitle>{section.title}</SectionTitle>
                <Instructions>Comment on existing datapoints…</Instructions>
                {section.data.map((datum, i) => {
                  const { value: currentValue, source, year } = datum;
                  const datumPredicate = (d: ExistingMetadata) =>
                    d.source === source && d.type === section.type && d.year === year;
                  const existingDatum = existingMetadata && existingMetadata.find(datumPredicate);
                  return (
                    <DataPointRow key={i}>
                      <FullRow>
                        <DataPointTitle>
                          {section.formatNumber(Number(currentValue))} {section.unit}{' '}
                          <Source>
                            {source}
                            {year != null ? `, ${year}` : ''}
                          </Source>
                        </DataPointTitle>
                        <RadioGroup
                          horizontal
                          onChange={(value: any) => {
                            const newValue: ExistingMetadata = {
                              type: section.type,
                              source,
                              currentValue,
                              year,
                              status: value as MetadataFeedbackStatus,
                            };
                            this.setState(state => ({
                              existingMetadata: replaceOrAddToArray(
                                state.existingMetadata || [],
                                datumPredicate,
                                newValue,
                              ),
                            }));
                          }}
                          value={existingDatum?.status || 'ok'}
                        >
                          <RadioButton
                            iconSize={20}
                            pointColor={BouColor.TOOL_TEXT_BLACK}
                            rootColor={BouColor.TOOL_DISABLED_GRAY}
                            iconInnerSize={10}
                            value="ok"
                          >
                            Looks ok
                          </RadioButton>
                          <RadioButton
                            iconSize={20}
                            pointColor={BouColor.TOOL_TEXT_BLACK}
                            rootColor={BouColor.TOOL_DISABLED_GRAY}
                            iconInnerSize={10}
                            value="high"
                          >
                            Too big
                          </RadioButton>
                          <RadioButton
                            iconSize={20}
                            pointColor={BouColor.TOOL_TEXT_BLACK}
                            rootColor={BouColor.TOOL_DISABLED_GRAY}
                            iconInnerSize={10}
                            value="low"
                          >
                            Too small
                          </RadioButton>
                        </RadioGroup>
                      </FullRow>
                    </DataPointRow>
                  );
                })}
                <h3>…or suggest new data points if you have more accurate or recent data</h3>
                {sectionNewData &&
                  sectionNewData.map((newDatum, i) => {
                    return (
                      <NewDataPointRow key={i}>
                        <NewDataPointTitle>
                          New {section.type} data point{' '}
                          <RemoveLink
                            onClick={() => {
                              this.setState(state => ({
                                newMetadata: state.newMetadata && state.newMetadata.filter(d => d !== newDatum),
                              }));
                            }}
                          >
                            (remove)
                          </RemoveLink>
                        </NewDataPointTitle>
                        <FormRow style={{ marginTop: 0 }}>
                          <TextField
                            label={`Suggested value${section.unit ? ` in ${section.unit}` : ''}`}
                            id={`newdata-${section.type}-${i}`}
                            type="text"
                            value={newDatum.value}
                            disabled={isSubmitting}
                            onChange={value => {
                              const newValue = {
                                ...newDatum,
                                value,
                              };
                              this.setState(state => ({
                                newMetadata: replaceOrAddToArray(
                                  state.newMetadata || [],
                                  d => d === newDatum,
                                  newValue,
                                ),
                              }));
                            }}
                          />
                          <TextField
                            label="Year"
                            id={`newdata-${section.type}-${i}-year`}
                            type="text"
                            value={newDatum.year}
                            disabled={isSubmitting}
                            onChange={value => {
                              const parsedValue = parseInt(value, 10);
                              if (!isNaN(parsedValue)) {
                                const newValue = {
                                  ...newDatum,
                                  year: parsedValue,
                                };
                                this.setState(state => ({
                                  newMetadata: replaceOrAddToArray(
                                    state.newMetadata || [],
                                    d => d === newDatum,
                                    newValue,
                                  ),
                                }));
                              }
                            }}
                          />
                        </FormRow>
                        <FormRow style={{ marginTop: themeDEPRECATED.rhythm(3) }}>
                          <TextField
                            label="Reference"
                            id={`newdata-${section.type}-${i}-source`}
                            type="text"
                            value={newDatum.source}
                            disabled={isSubmitting}
                            onChange={value => {
                              const newValue = {
                                ...newDatum,
                                source: value,
                              };
                              this.setState(state => ({
                                newMetadata: replaceOrAddToArray(
                                  state.newMetadata || [],
                                  d => d === newDatum,
                                  newValue,
                                ),
                              }));
                            }}
                          />
                        </FormRow>
                      </NewDataPointRow>
                    );
                  })}
                <NewDataPointRow>
                  <FormRow style={{ marginTop: 0 }}>
                    <NewDataPointLink
                      onClick={() => {
                        this.setState(state => ({
                          newMetadata: (state.newMetadata || []).concat([
                            {
                              value: '',
                              type: section.type,
                              source: '',
                              year: new Date().getFullYear(),
                            },
                          ]),
                        }));
                      }}
                    >
                      + Suggest a new data point
                    </NewDataPointLink>
                  </FormRow>
                </NewDataPointRow>
              </Section>
            );
          })}
          <FormRow>
            <TextField
              label="Anything else you want to share?"
              id="comment"
              type="textarea"
              value={comment}
              disabled={isSubmitting}
              metaLabel="Optional"
              onChange={value => {
                this.setState({ comment: value });
              }}
            />
          </FormRow>
          <ContributionModalParagraph style={{ marginTop: themeDEPRECATED.rhythm(5) }}>
            Please note that contributions will not automatically affect the data in the model, but will be processed
            and evaluated either by {aiName} or the Upright team.
          </ContributionModalParagraph>
          <FormRow>
            <FormButton
              type="submit"
              disabled={isSubmitting || !canSubmit}
              onClick={() => {
                this.props.submitContribution({
                  data: {
                    type: 'COMPANY_INVALID_METADATA',
                    comment: this.state.comment,
                    existingData: this.state.existingMetadata || [],
                    newData: this.state.newMetadata || [],
                  },
                  target,
                });
              }}
              style={{ width: '100%' }}
            >
              Submit
            </FormButton>
          </FormRow>
        </form>
      </>
    );
  }
}
